import { checkAssetTransport } from './assetTransport';
/**
 * Desktop offline routing data package management.
 *
 * Mirrors api/offlineMap.js. Routing packages install, update, and roll back
 * the same way map packages do, so the client shapes match deliberately.
 *
 * See TCA-26081002.
 */
import axios from 'axios';
import { localBackendUrl } from './localBackend';

export const getRoutingDataStatus = async () => {
  const baseUrl = localBackendUrl();
  if (!baseUrl) {
    return {
      installed: false,
      state: 'desktop_unavailable',
      detail: 'Local desktop backend is unavailable.',
    };
  }
  const response = await axios.get(`${baseUrl}/api/desktop/offline-routing/status`);
  return response.data;
};

export const getRoutingDataUpdateStatus = async (context) => {
  checkAssetTransport(context);
  const baseUrl = context ? context.baseUrl : localBackendUrl();
  if (!baseUrl) {
    return {
      checked: false,
      local: {
        installed: false,
        state: 'desktop_unavailable',
        detail: 'Local desktop backend is unavailable.',
      },
      published: null,
    };
  }
  const response = await axios.get(`${baseUrl}/api/desktop/offline-routing/update-status`, ...(context ? [{ ...context.config, timeout: 30000 }] : []));
  return response.data;
};

/**
 * Hand a routing install or rollback to the desktop main process.
 *
 * Both stage across a restart, and the window that started them is closed
 * before staging begins — so the request cannot be owned by this page. The
 * main process confirms with the operator, stages, and relaunches.
 *
 * A staged acknowledgement is restart handoff, not installation completion.
 * A missing/malformed native acknowledgement must never fall through to HTTP.
 */
const runThroughDesktop = (kind, context) => {
  const desktop = context ? context.bridge : (typeof window !== 'undefined' ? window.lvbDesktop : null);
  if (typeof desktop?.runRoutingInstall !== 'function') return { handled: false };

  // Electron obtains authorization from its authoritative in-memory cloud
  // session. No bearer token crosses this renderer IPC boundary.
  return { handled: true, result: desktop.runRoutingInstall(kind) };
};

export const installRoutingData = async (context) => {
  checkAssetTransport(context);
  const orchestrated = runThroughDesktop('install', context);
  if (orchestrated.handled) return orchestrated.result;

  const baseUrl = context ? context.baseUrl : localBackendUrl();
  if (!baseUrl) throw new Error('Local desktop backend is unavailable');
  const response = await axios.post(`${baseUrl}/api/desktop/offline-routing/install`, ...(context ? [undefined, context.config] : []));
  return response.data;
};

export const rollbackRoutingData = async (context) => {
  checkAssetTransport(context);
  const orchestrated = runThroughDesktop('rollback', context);
  if (orchestrated.handled) return orchestrated.result;

  const baseUrl = context ? context.baseUrl : localBackendUrl();
  if (!baseUrl) throw new Error('Local desktop backend is unavailable');
  const response = await axios.post(`${baseUrl}/api/desktop/offline-routing/rollback`, ...(context ? [undefined, context.config] : []));
  return response.data;
};

/**
 * Turn a status payload into what the panel should say.
 *
 * Kept as a pure function so the wording is testable, and so the distinction
 * that matters most is enforced in one place: a machine with no routing data
 * has an outstanding setup step, not a fault. Presenting that as an error makes
 * every new install look broken on first run.
 */
export const describeRoutingState = (updateStatus) => {
  const local = updateStatus?.local || {};
  const published = updateStatus?.published || null;

  if (local.state === 'desktop_unavailable') {
    return { tone: 'muted', headline: 'Routing data unavailable', detail: local.detail, canInstall: false };
  }

  if (updateStatus?.checked && published && updateStatus.compatible === false) {
    return {
      tone: 'warning',
      headline: 'Routing update unavailable',
      detail: updateStatus.detail,
      canInstall: false,
    };
  }

  if (local.state === 'not_installed') {
    return {
      tone: 'setup',
      headline: 'No routing data installed',
      detail: 'Offline route lines need routing data. Install it to enable them.',
      canInstall: true,
    };
  }

  if (local.state === 'installed_unmanaged') {
    return {
      tone: 'setup',
      headline: 'Routing data installed manually',
      detail: 'Routing works, but this copy was not installed by the app and cannot be updated. Installing will bring it under app management.',
      canInstall: true,
    };
  }

  if (local.state === 'installed' && updateStatus?.updateAvailable) {
    return {
      tone: 'update',
      headline: `Routing data ${local.version} — update available`,
      detail: published ? `Version ${published.version} is available.` : null,
      canInstall: true,
      canRollback: Boolean(local.rollbackAvailable),
    };
  }

  if (local.state === 'installed') {
    return {
      tone: 'ok',
      headline: `Routing data ${local.version} installed`,
      detail: updateStatus?.checked ? 'Up to date.' : 'Could not check for updates.',
      canInstall: false,
      canRollback: Boolean(local.rollbackAvailable),
    };
  }

  return { tone: 'muted', headline: 'Routing data status unknown', detail: local.detail, canInstall: false };
};
